import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = article ? getRelatedArticles(article, 3) : [];

  if (!article) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
        <h1 className="text-2xl font-heading text-foreground-900 font-bold mb-4">文章未找到</h1>
        <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '黄游戏',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://huangyouxi.com/article/${article.slug}`,
    },
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />

      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: article.category },
        ]}
      />

      <article>
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span
              className={`px-2.5 py-1 rounded-md text-xs font-medium text-background-50 ${
                article.categorySlug === 'guide'
                  ? 'bg-primary-500'
                  : article.categorySlug === 'ranking'
                    ? 'bg-accent-500'
                    : 'bg-secondary-500'
              }`}
            >
              {article.category}
            </span>
            {article.gameName && (
              <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium">
                {article.gameName}
              </span>
            )}
          </div>

          <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold leading-tight mb-4">
            {article.title}
          </h1>

          <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
          </div>
        </div>

        <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden mb-8">
          <img
            src={article.coverImageUrl}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div
          className="prose prose-lg max-w-none text-foreground-700 leading-relaxed
            [&_h3]:font-heading [&_h3]:text-xl [&_h3]:text-foreground-900 [&_h3]:font-bold [&_h3]:mt-8 [&_h3]:mb-4
            [&_p]:mb-4 [&_p]:leading-loose
            [&_b]:text-foreground-900
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4
            [&_li]:mb-2"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <div className="flex items-center gap-2 flex-wrap mt-8 pt-6 border-t border-background-200">
          {article.tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
            >
              #{tag}
            </span>
          ))}
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="mt-12 pt-8 border-t border-background-200">
          <h3 className="font-heading text-xl text-foreground-900 font-bold mb-6">
            相关推荐
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {relatedArticles.map((related) => (
              <Link
                key={related.id}
                to={`/article/${related.slug}`}
                className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300/60 transition-all duration-300 cursor-pointer"
              >
                <div className="relative w-full aspect-[16/9] overflow-hidden">
                  <img
                    src={related.coverImageUrl}
                    alt={related.title}
                    title={related.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h4 className="font-heading text-sm text-foreground-900 font-bold group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mb-2">
                    {related.title}
                  </h4>
                  <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                    {related.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}